import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  CalendarDaysIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  DocumentCurrencyEuroIcon,
  BellAlertIcon,
  Cog6ToothIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  StarIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';
import Layout from '../../components/Layout';

export default function AdminLayout() {
  // Collapsed state persists for non-mobile
  const [collapsed, setCollapsed] = useState(false);
  const [role, setRole] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const saved = localStorage.getItem('adminSidebarCollapsed');
    if (saved !== null) setCollapsed(saved === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const user = u?.user ?? null;

      if (!user) {
        navigate('/login');
        return;
      }

      const { data: prof } = await supabase.from('profiles').select('type').eq('id', user.id).single();
      const userRole = prof?.type ?? null;

      if (userRole !== 'admin' && userRole !== 'staff') {
        navigate('/');
        return;
      }

      if (userRole === 'staff' && (location.pathname === '/admin' || location.pathname === '/admin/')) {
        navigate('/admin/services', { replace: true });
      }

      setRole(userRole);
      setCheckingRole(false);
    })();
  }, [navigate]);

  const isAdmin = role === 'admin';

  if (checkingRole) {
    return (
      <Layout page="admin">
        <div className="flex items-center justify-center h-screen mt-12 pt-12 text-ink/60">Verificando acceso…</div>
      </Layout>
    );
  }

  return (
    <Layout page="admin">
      <div className="flex h-screen mt-12 pt-12">
        {/* ────────── Left drawer ────────── */}
        <aside
          className={
            // Width collapses on sm+; on mobile it's always icon-only already
            `flex-shrink-0 bg-cream border-r border-sand transition-[width] duration-200 ease-in-out ` +
            `w-16 ${collapsed ? 'sm:w-20' : 'sm:w-56'}`
          }
        >
          {/* Collapse toggle (only visible on sm+) */}
          <div className="hidden sm:flex items-center justify-end p-2">
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-terracotta px-2 py-1 rounded-md hover:bg-sand"
              aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
              title={collapsed ? 'Expandir' : 'Colapsar'}
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="w-5 h-5" />
              ) : (
                <ChevronDoubleLeftIcon className="w-5 h-5" />
              )}
              {/* Hide label when collapsed */}
              <span className={collapsed ? 'hidden' : 'inline'}>Menú</span>
            </button>
          </div>

          <nav className="flex flex-col gap-1 p-2 sm:p-4">
            {isAdmin && (
              <>
                <AdminLink
                  to="calendar"
                  icon={<CalendarDaysIcon className="w-6 h-6" />}
                  label="Calendario"
                  collapsed={collapsed}
                />
                <AdminLink
                  to="clients"
                  icon={<UsersIcon className="w-6 h-6" />}
                  label="Clientes"
                  collapsed={collapsed}
                />
                <AdminLink
                  to="classes"
                  icon={<ClipboardDocumentListIcon className="w-6 h-6" />}
                  label="Clases"
                  collapsed={collapsed}
                />
              </>
            )}
            <AdminLink
              to="services"
              icon={<BriefcaseIcon className="w-6 h-6" />}
              label="Servicios"
              collapsed={collapsed}
            />
            {isAdmin && (
              <>
                <AdminLink
                  to="bonus-types"
                  icon={<DocumentCurrencyEuroIcon className="w-6 h-6" />}
                  label="Tipos de Bonos"
                  collapsed={collapsed}
                />
                <AdminLink
                  to="trial-requests"
                  icon={<StarIcon className="w-6 h-6" />}
                  label="Clases de Prueba"
                  collapsed={collapsed}
                />
                <AdminLink
                  to="notifications"
                  icon={<BellAlertIcon className="w-6 h-6" />}
                  label="Notificaciones"
                  collapsed={collapsed}
                />
                <AdminLink
                  to="config"
                  icon={<Cog6ToothIcon className="w-6 h-6" />}
                  label="Configuración"
                  collapsed={collapsed}
                />
              </>
            )}
          </nav>
        </aside>

        {/* ────────── Routed page ────────── */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </Layout>
  );
}

function AdminLink({ to, icon, label, collapsed }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative ${
          isActive ? 'bg-sand text-terracotta-dark font-semibold' : 'text-ink'
        } flex items-center justify-center sm:justify-start gap-0 sm:gap-2 px-2 py-2 sm:px-3 sm:py-2 rounded-md hover:bg-sand/70 hover:text-terracotta-dark transition-colors`
      }
      title={label}
      aria-label={label}
    >
      {icon}
      {/* Hide text on mobile always; on sm+ hide when collapsed */}
      <span className={`hidden ${collapsed ? '' : 'sm:inline'}`}>{/* spacer to keep layout */}</span>
      <span className={`hidden sm:inline ${collapsed ? 'sr-only' : ''}`}>{label}</span>
    </NavLink>
  );
}
